import {
  readBrowserSafeEnv,
  readBrowserSafeNodeEnv,
} from "@/lib/browser-safe-env";

export const OFFICIAL_APP_URL = "https://app.hiive.club";

const LOOPBACK_HOSTS = new Set([
  "localhost",
  "127.0.0.1",
  "0.0.0.0",
  "10.0.2.2",
  "::1",
]);

export function normalizeAppOrigin(value?: string | null) {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  try {
    return new URL(trimmed).origin;
  } catch {
    return null;
  }
}

export function isLoopbackAppUrl(value: string) {
  try {
    const url = new URL(value);
    return LOOPBACK_HOSTS.has(url.hostname);
  } catch {
    return false;
  }
}

export function getPublicAppOrigin() {
  const nodeEnv = readBrowserSafeNodeEnv();
  const configured = normalizeAppOrigin(
    readBrowserSafeEnv("NEXT_PUBLIC_APP_URL"),
  );
  if (configured && !isLoopbackAppUrl(configured)) return configured;

  if (typeof window !== "undefined") {
    const currentOrigin = normalizeAppOrigin(window.location.origin);
    if (
      currentOrigin &&
      (nodeEnv !== "production" || !isLoopbackAppUrl(currentOrigin))
    ) {
      return currentOrigin;
    }
  }

  if (nodeEnv !== "production" && configured) {
    return configured;
  }

  return OFFICIAL_APP_URL;
}

export function absolutePublicAppUrl(path = "/") {
  if (/^https?:\/\//i.test(path)) return path;
  return new URL(
    path.startsWith("/") ? path : `/${path}`,
    getPublicAppOrigin(),
  ).toString();
}

/**
 * Absolute target for server-issued redirects (Route Handlers, middleware).
 *
 * Never build these off `request.url`: behind the reverse proxy the
 * standalone server ignores Host entirely and `request.url` carries the
 * listen address (http://0.0.0.0:3000) instead — every redirect built on it
 * leaves the site and downgrades to http. Seen in production, where Google
 * sign-in landed on https://0.0.0.0:3000/hiives. The canonical origin is
 * also spoof-proof, which a Host header never is.
 */
export function publicRedirectTarget(path: string): URL {
  const origin = getPublicAppOrigin();
  const fallback = new URL("/", origin);
  try {
    if (!path.startsWith("/")) {
      if (/^https?:\/\//i.test(path)) return fallback;
      const relative = new URL(path, fallback);
      if (
        relative.origin === origin &&
        !relative.username &&
        !relative.password
      ) {
        return relative;
      }
    } else if (path.startsWith("//") || path.startsWith("/\\")) {
      return fallback;
    } else {
      const target = new URL(path, origin);
      if (target.origin === origin && !target.username && !target.password) {
        return target;
      }
    }
  } catch {
    return fallback;
  }
  return fallback;
}
